import { AxiosResponse } from 'axios';
import { useContext } from 'react';

import AppContext from '@/contexts/appContext';
import {
  ActiveTask,
  CeleryConfig,
  CeleryOverview,
  PeekMessage,
  PurgeResult,
  QueueBreakdown,
  RemoveResult,
  RestoreResult,
  TaskStatusRecord,
  UnackedEntry
} from '@/types/celeryConsole';
import ajax from '@/utils/ajax';

const BASE = '/api/dugout/celery_console';

export const celeryConsoleRepository = {
  async getOverview({ host }: { host: string }): Promise<CeleryOverview> {
    const response = await ajax.get({ url: `${host}${BASE}/overview/` });
    return response.data;
  },

  async getConfig({ host }: { host: string }): Promise<CeleryConfig> {
    const response = await ajax.get({ url: `${host}${BASE}/config/` });
    return response.data;
  },

  async getUnacked({ host }: { host: string }): Promise<UnackedEntry[]> {
    const response = await ajax.get({ url: `${host}${BASE}/unacked/` });
    return response.data;
  },

  async getActive({ host }: { host: string }): Promise<ActiveTask[]> {
    const response = await ajax.get({ url: `${host}${BASE}/active/` });
    return response.data;
  },

  async peekQueue({
    host,
    queue,
    limit
  }: {
    host: string;
    queue: string;
    limit?: number;
  }): Promise<PeekMessage[]> {
    const response = await ajax.get({
      url: `${host}${BASE}/peek/`,
      params: { queue, limit }
    });
    return response.data;
  },

  async getQueueBreakdown({
    host,
    queue,
    perEnd,
    full
  }: {
    host: string;
    queue: string;
    perEnd?: number;
    full?: boolean;
  }): Promise<QueueBreakdown> {
    const response = await ajax.get({
      url: `${host}${BASE}/queue_breakdown/`,
      params: { queue, per_end: perEnd, ...(full ? { mode: 'full' } : {}) }
    });
    return response.data;
  },

  async getRecentTaskStatuses({
    host,
    limit
  }: {
    host: string;
    limit?: number;
  }): Promise<TaskStatusRecord[]> {
    const response = await ajax.get({
      url: `${host}${BASE}/task_status_recent/`,
      params: { limit }
    });
    return response.data;
  },

  async getTaskStatus({ host, id }: { host: string; id: string }): Promise<TaskStatusRecord> {
    const response = await ajax.get({
      url: `${host}${BASE}/task_status/`,
      params: { id }
    });
    return response.data;
  },

  async restore({
    host,
    deliveryTags
  }: {
    host: string;
    deliveryTags: string[];
  }): Promise<RestoreResult> {
    const response = await ajax.post<{ delivery_tags: string[] }, AxiosResponse<RestoreResult>>({
      url: `${host}${BASE}/restore/`,
      data: { delivery_tags: deliveryTags }
    });
    return response.data;
  },

  async remove({
    host,
    deliveryTags
  }: {
    host: string;
    deliveryTags: string[];
  }): Promise<RemoveResult> {
    const response = await ajax.post<{ delivery_tags: string[] }, AxiosResponse<RemoveResult>>({
      url: `${host}${BASE}/remove/`,
      data: { delivery_tags: deliveryTags }
    });
    return response.data;
  },

  async purgeUnacked({ host, taskPath }: { host: string; taskPath: string }): Promise<PurgeResult> {
    const response = await ajax.post<{ task_path: string }, AxiosResponse<PurgeResult>>({
      url: `${host}${BASE}/purge_unacked/`,
      data: { task_path: taskPath }
    });
    return response.data;
  },

  async purgeByTaskPath({
    host,
    queue,
    taskPath
  }: {
    host: string;
    queue: string;
    taskPath: string;
  }): Promise<PurgeResult> {
    const response = await ajax.post<
      { queue: string; task_path: string },
      AxiosResponse<PurgeResult>
    >({
      url: `${host}${BASE}/purge/`,
      data: { queue, task_path: taskPath }
    });
    return response.data;
  }
};

const CeleryConsoleRepository = () => {
  const { host } = useContext(AppContext);

  return {
    getOverview: (): Promise<CeleryOverview> => celeryConsoleRepository.getOverview({ host }),
    getConfig: (): Promise<CeleryConfig> => celeryConsoleRepository.getConfig({ host }),
    getUnacked: (): Promise<UnackedEntry[]> => celeryConsoleRepository.getUnacked({ host }),
    getActive: (): Promise<ActiveTask[]> => celeryConsoleRepository.getActive({ host }),
    peekQueue: (queue: string, limit?: number): Promise<PeekMessage[]> =>
      celeryConsoleRepository.peekQueue({ host, queue, limit }),
    getQueueBreakdown: (queue: string, perEnd?: number, full?: boolean): Promise<QueueBreakdown> =>
      celeryConsoleRepository.getQueueBreakdown({ host, queue, perEnd, full }),
    getRecentTaskStatuses: (limit?: number): Promise<TaskStatusRecord[]> =>
      celeryConsoleRepository.getRecentTaskStatuses({ host, limit }),
    getTaskStatus: (id: string): Promise<TaskStatusRecord> =>
      celeryConsoleRepository.getTaskStatus({ host, id }),
    restore: (deliveryTags: string[]): Promise<RestoreResult> =>
      celeryConsoleRepository.restore({ host, deliveryTags }),
    remove: (deliveryTags: string[]): Promise<RemoveResult> =>
      celeryConsoleRepository.remove({ host, deliveryTags }),
    purgeUnacked: (taskPath: string): Promise<PurgeResult> =>
      celeryConsoleRepository.purgeUnacked({ host, taskPath }),
    purgeByTaskPath: (queue: string, taskPath: string): Promise<PurgeResult> =>
      celeryConsoleRepository.purgeByTaskPath({ host, queue, taskPath })
  };
};

export default CeleryConsoleRepository;
